import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as yup from "yup";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { useFormik } from "formik";
import moment from "moment/moment";
import { useEffect, useState } from "react";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Header from "../../components/Header";
import UserRoleApi from "../../services/api/UserRole";
import RoleApi from "../../services/api/roles";
import UsersApi from "../../services/api/users";
import { tokens } from "../../theme";

const UserRole = () => {
  const isSmallScreen = useMediaQuery("(max-width: 767px)");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isEdit, setIsEdit] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userId, setUserId] = useState();
  const [userRoles, setUserRoles] = useState([]);

  const columns = [
    {
      field: "roleName",
      headerName: "Role",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "expireDate",
      headerName: "Expire Date",
      valueFormatter: (params) => {
        return moment(params.value).format("DD-MM-YYYY");
      },
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "",
      headerName: "Action",
      width: 200,
      renderCell: ({ row }) => {
        return (
          <Box
            display="flex"
            gap={2}
            justifyContent="center"
            borderRadius="4px"
          >
            <Box display="flex">
              <EditOutlinedIcon
                fontSize="small"
                style={{ marginRight: "4px" }}
              />
              <Typography
                variant="button"
                color="warning"
                onClick={() => edit(row)}
                style={{ cursor: "pointer", marginRight: "10px" }}
              >
                Edit
              </Typography>
            </Box>

            <Box display="flex">
              <DeleteOutlineOutlinedIcon
                fontSize="small"
                color="error"
                style={{ marginRight: "4px" }}
              />
              <Typography
                variant="button"
                color="error"
                onClick={() => destroy(row)}
                style={{ cursor: "pointer" }}
              >
                Delete
              </Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: checkoutSchema,

    onSubmit: async (values, { resetForm }) => {
      if (isEdit) {
        updateEntity(values);
      } else {
        createEntity(values);
      }

      resetForm({ values: { ...initialValues, user_id: userId } });
      handleAfterReset();
    },
  });

  const createEntity = async (values) => {
    const response = await UserRoleApi().create(values);

    console.log(createdSuccessMsg, response);
    fetchUserRoles(userId);
  };

  const updateEntity = async (values) => {
    const response = await UserRoleApi().edit(values.id, values);

    console.log(updatedSuccessMsg, response);
    fetchUserRoles(userId);
  };

  const handleAfterReset = () => {
    setIsEdit(false);
  };

  const fetchUsers = async () => {
    try {
      const data = await UsersApi().getAllUsers();

      setUsers(data.users);
    } catch (error) {
      console.log("Not loading users", error);
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await RoleApi().getAll();

      setRoles(data.results);
    } catch (error) {
      console.log("Not loading users", error);
    }
  };

  const handleUserChange = (event) => {
    setUserId(event.target.value);
    fetchUserRoles(event.target.value);
    formik.handleChange(event);
  };

  const edit = (row) => {
    if (row) {
      formik.setValues({
        id: row.id,
        user_id: row.userId,
        role_id: row.roleId,
        expire_date: moment(row.expireDate).format("YYYY-MM-DD"),
      });

      setIsEdit(true);
    }
  };

  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const destroy = (row) => {
    setSelectedId(row.id);
    handleOpenConfirmation();
  };

  const handleOpenConfirmation = () => {
    setConfirmationOpen(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
  };

  const handleConfirmAction = () => {
    try {
      const response = UserRoleApi().destroy(selectedId);

      console.log(deletedSuccessMsg, response);
      fetchUserRoles(userId);
    } catch (error) {
      console.log(deleteErrorMsg, error);
    }

    handleCloseConfirmation();
  };

  const fetchUserRoles = async (userId) => {
    try {
      const data = await UserRoleApi().getByUserId(userId);

      setUserRoles(data);
    } catch (error) {
      setUserRoles([]);
      if (error.response.status === 404) {
        console.log("This user has no roles.");
      } else {
        console.log("Loading error", error.response);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          padding: "15px 30px",
          borderBottom: "1px solid var(--color-primary-200)",
        }}
      >
        <Header title="User Role Assignment" />
      </Box>

      <Box m="30px" display="flex" flexDirection="column" flex={1}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: "15px",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, display: "flex", gap: 2 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={formik.values.user_id || ""}
                  onChange={handleUserChange}
                  name="user_id"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span className="placeholder-text">Select User</span>
                      );
                    }
                    return users?.find((u) => u.id === selected)?.name;
                  }}
                >
                  <MenuItem value="" disabled>
                    Select User
                  </MenuItem>

                  {users?.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <Select
                  value={formik.values.role_id || ""}
                  onChange={formik.handleChange}
                  name="role_id"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span className="placeholder-text">Select Role</span>
                      );
                    }
                    return roles?.find((r) => r.id === selected)?.name;
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Role
                  </MenuItem>

                  {roles?.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="date"
                name="expire_date"
                value={formik.values.expire_date || ""}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={
                  !!formik.touched.expire_date && !!formik.errors.expire_date
                }
                helperText={
                  formik.touched.expire_date && formik.errors.expire_date
                }
                InputLabelProps={{
                  shrink: true, // prevents layout glitch
                }}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              sx={{
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {isEdit ? "Update" : "Add User"}
            </Button>
          </Box>

          <Box height="75vh">
            {userRoles && (
              <DataGrid
                rows={userRoles}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                columnHeaderHeight={isSmallScreen ? 70 : 60}
                sx={{
                  "& .MuiDataGrid-columnHeader--moving": {
                    backgroundColor: "inherit !important",
                  },
                  ...(isSmallScreen && {
                    "& .MuiDataGrid-columnSeparator": {
                      display: "none",
                    },
                    "& .MuiDataGrid-sortIcon": {
                      display: "none",
                    },
                    "& .MuiDataGrid-menuIcon": {
                      display: "none",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      padding: "4px !important",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                    "& .MuiDataGrid-columnHeaderTitleContainer": {
                      padding: "0 !important",
                      margin: "0 !important",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      whiteSpace: "normal !important",
                      wordBreak: "normal",
                      textAlign: "center",
                      lineHeight: 1.2,
                    },
                  }),
                }}
              />
            )}
          </Box>
        </form>
      </Box>
      <ConfirmationDialog
        open={isConfirmationOpen}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmAction}
      />
    </>
  );
};

const checkoutSchema = yup.object().shape({
  expire_date: yup.date().required("required"),
  role_id: yup.number().required("required"),
  user_id: yup.number().required("required"),
});

const initialValues = {
  user_id: "",
  role_id: "",
  expire_date: moment().format("YYYY-MM-DD"),
};

const formName = "User Role";
const createdSuccessMsg = `${formName} created:`;
const updatedSuccessMsg = `${formName} updated:`;
const deletedSuccessMsg = `${formName} deleted successfully: `;
const deleteErrorMsg = `Not deleted ${formName}`;

export default UserRole;
